/**
 * API Query Hook using TanStack Query + fetch
 * Facade pattern for React SDK
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// Global API client instance
let globalApiClient = null;
export function setGlobalApiClient(client) {
    globalApiClient = client;
}
export function getGlobalApiClient() {
    if (!globalApiClient) {
        throw new Error('API client not initialized. Make sure TagadaProvider is wrapping your app.');
    }
    return globalApiClient;
}
export function getGlobalApiClientOrNull() {
    return globalApiClient;
}
// Query key builders for consistency
export const queryKeys = {
    checkout: (token, currency) => ['checkout', token, currency].filter(Boolean),
    promotions: (sessionId) => ['promotions', sessionId],
    discounts: (sessionId) => ['discounts', sessionId],
    orderBumps: (sessionId) => ['order-bumps', sessionId],
    shippingRates: (sessionId) => ['shipping-rates', sessionId],
    vipPreview: (sessionId, offerIds) => ['vip-preview', sessionId, offerIds],
    products: (productIds, options) => ['products', productIds, options],
    store: (storeId) => ['store', storeId],
    storeConfig: (storeId) => ['storeConfig', storeId],
    order: (orderId) => ['order', orderId],
};
// Main query hook
export function useApiQuery(key, url, options) {
    const client = getGlobalApiClient();
    return useQuery({
        queryKey: key,
        queryFn: async () => {
            if (!url)
                throw new Error('URL is required');
            return client.get(url);
        },
        enabled: !!url && (options?.enabled !== false),
        staleTime: 30000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime in v4)
        ...options,
    });
}
// Mutation hook
export function useApiMutation(mutationFn, options) {
    return useMutation({
        mutationFn,
        ...options,
    });
}
// Convenience hooks for common operations
export function useInvalidateQuery() {
    const queryClient = useQueryClient();
    return {
        invalidateCheckout: (token) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.checkout(token) });
        },
        invalidatePromotions: (sessionId) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.promotions(sessionId) });
        },
        invalidateOrderBumps: (sessionId) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.orderBumps(sessionId) });
        },
        invalidateAll: () => {
            void queryClient.invalidateQueries();
        },
    };
}
// Preload function for optimistic loading
export function usePreloadQuery() {
    const queryClient = useQueryClient();
    const client = getGlobalApiClient();
    return {
        preloadCheckout: (token, currency) => {
            const url = `/api/v1/checkout-sessions/${token}/v2${currency ? `?currency=${currency}` : ''}`;
            void queryClient.prefetchQuery({
                queryKey: queryKeys.checkout(token, currency),
                queryFn: () => client.get(url),
                staleTime: 30000,
            });
        },
    };
}
